import { z } from "zod/v4"

import type {
	AIActionToolInput,
	AiChatMessage,
	AICiteItem,
	AICiteToolInput,
} from "./ai/schema"
import { parseAiStream } from "./ai-stream-parser"
import {
	dedupeConnectorAttributions,
	readConnectorAttribution,
} from "./ai/connector-attribution"
import { isRestatement, REDUNDANT_BLOCK_MAX_CHARS } from "./ai/redundant-text"
import {
	AI_CHAT_HANDOFF_CHECK_PART,
	aiChatHandoffCheckSchema,
	aiCiteToolInputSchema,
	aiImageSpecSchema,
} from "./ai/schema"
import {
	AGENT_SETUP_TOOL_PROPOSE_INSTRUCTIONS,
	AGENT_SETUP_TOOL_PROPOSE_LINEAR_ISSUE,
	CITE_TOOL_ADD_SOURCE,
	DOCS_TOOL_SHOW_IMAGE,
	getToolDisplayName,
	VISITOR_EMAIL_TOOL_REQUEST,
} from "./ai/tools"

// A day of silence ends an AI chat conversation. The next turn is a fresh ask.
export const AI_CHAT_QUIET_PERIOD_MS = 24 * 60 * 60 * 1000

// AI chat thread messages carry no role, so the agent's replies are told apart by this actor name.
export const AI_CHAT_ACTOR_NAME = "Productlane Agent"

// Both texts are optional: a customer-facing surface sends them only when the
// agent wrote them in this conversation, and then it sets agentAuthored.
const linearIssueSchema = z.object({
	id: z.string(),
	identifier: z.string(),
	title: z.string().nullish(),
	description: z.string().nullish(),
	important: z.boolean().nullish(),
	agentAuthored: z.boolean().nullish(),
})

interface ToolOutputHandler {
	handle: (
		output: unknown,
		message: AiChatMessage,
		allMessages?: AiChatMessage[],
	) => void
}

function toolOutput<T extends z.ZodType>(
	schema: T,
	apply: (
		data: z.infer<T>,
		message: AiChatMessage,
		allMessages?: AiChatMessage[],
	) => void,
): ToolOutputHandler {
	return {
		handle: (output, message, allMessages) => {
			const parsed = schema.safeParse(output)
			if (parsed.success) apply(parsed.data as z.infer<T>, message, allMessages)
		},
	}
}

export const toolOutputMap: Record<string, ToolOutputHandler> = {
	[DOCS_TOOL_SHOW_IMAGE]: toolOutput(
		z.object({ image: aiImageSpecSchema }),
		(data, msg) => {
			msg.images = { ...msg.images, [data.image.id]: data.image }
		},
	),
	[VISITOR_EMAIL_TOOL_REQUEST]: toolOutput(
		z.object({ emailRequested: z.literal(true) }),
		(_, msg) => {
			msg.emailRequested = true
		},
	),
	createLinearIssue: toolOutput(linearIssueSchema, (data, msg) => {
		msg.linearIssueCreated = {
			id: data.id,
			identifier: data.identifier,
			title: data.title ?? undefined,
			description: data.description ?? undefined,
			agentAuthored: data.agentAuthored ?? undefined,
		}
	}),
	editLinearIssue: toolOutput(linearIssueSchema, (data, msg) => {
		msg.linearIssueEdited = {
			id: data.id,
			identifier: data.identifier,
			title: data.title ?? undefined,
			description: data.description ?? undefined,
			important: data.important ?? undefined,
			agentAuthored: data.agentAuthored ?? undefined,
		}
	}),
	removeLinearIssue: toolOutput(
		z.object({
			success: z.boolean(),
			identifier: z.string(),
			title: z.string().optional(),
		}),
		(data, msg) => {
			msg.linearIssueRemoved = {
				identifier: data.identifier,
				title: data.title,
			}
		},
	),
	linkExistingIssue: toolOutput(
		z.object({
			id: z.string(),
			identifier: z.string(),
			title: z.string().nullish(),
			description: z.string().nullish(),
			status: z.string().nullish(),
			type: z.enum(["issue", "project"]),
			linearId: z.string(),
			important: z.boolean().nullish(),
		}),
		(data, msg) => {
			msg.linearIssueLinked = {
				id: data.id,
				identifier: data.identifier,
				title: data.title ?? undefined,
				description: data.description ?? undefined,
				status: data.status ?? undefined,
				type: data.type,
				linearId: data.linearId,
				important: data.important ?? undefined,
			}
		},
	),
	writeToComposer: toolOutput(
		z.object({
			action: z.literal("writeToComposer"),
			rewrittenHtml: z.string(),
			description: z.string().optional(),
		}),
		(data, msg) => {
			msg.contentRewrite = {
				target: "composer",
				html: data.rewrittenHtml,
				description: data.description,
			}
		},
	),
	writeToArticle: toolOutput(
		z.object({
			action: z.literal("writeToArticle"),
			html: z.string(),
			title: z.string().optional(),
			description: z.string().optional(),
		}),
		(data, msg) => {
			msg.contentRewrite = {
				target: "article",
				html: data.html,
				title: data.title,
				description: data.description,
			}
		},
	),
	writeToChangelog: toolOutput(
		z.object({
			action: z.literal("writeToChangelog"),
			html: z.string(),
			title: z.string().optional(),
			description: z.string().optional(),
		}),
		(data, msg) => {
			msg.contentRewrite = {
				target: "changelog",
				html: data.html,
				title: data.title,
				description: data.description,
			}
		},
	),
	proposeDocsUpdate: toolOutput(
		z.object({
			summary: z.string(),
			issueIds: z.array(z.string()).default([]),
			issueIdentifiers: z.array(z.string()).optional(),
			prUrls: z.array(z.string()).default([]),
			proposalGroupId: z.string().optional(),
			status: z.enum(["generating", "error"]).default("generating"),
			errorMessage: z.string().optional(),
		}),
		(data, msg) => {
			msg.docsProposalSuggestion = {
				summary: data.summary,
				issueIds: data.issueIds,
				issueIdentifiers: data.issueIdentifiers,
				prUrls: data.prUrls,
				proposalGroupId: data.proposalGroupId,
				status: data.status,
				errorMessage: data.errorMessage,
			}
		},
	),
	proposeContinueInClaude: toolOutput(
		z.object({ summary: z.string() }),
		(data, msg) => {
			msg.continueInClaude = { summary: data.summary }
		},
	),
	showAgentTodos: toolOutput(z.object({ shownAt: z.string() }), (data, msg) => {
		msg.agentTodos = { shownAt: data.shownAt }
	}),
	proposeApplyPromotionCode: toolOutput(
		z.object({
			status: z.literal("awaiting_approval"),
			pendingActionId: z.string(),
			summary: z.string(),
			previewData: z.record(z.string(), z.unknown()),
		}),
		(data, msg) => {
			msg.stripeActionApproval = {
				pendingActionId: data.pendingActionId,
				actionType: "apply_promotion_code",
				summary: data.summary,
				preview: data.previewData,
				status: "pending",
			}
		},
	),
	proposeRemovePromotionCode: toolOutput(
		z.object({
			status: z.literal("awaiting_approval"),
			pendingActionId: z.string(),
			summary: z.string(),
			previewData: z.record(z.string(), z.unknown()),
		}),
		(data, msg) => {
			msg.stripeActionApproval = {
				pendingActionId: data.pendingActionId,
				actionType: "remove_promotion_code",
				summary: data.summary,
				preview: data.previewData,
				status: "pending",
			}
		},
	),
	proposeCancelSubscription: toolOutput(
		z.object({
			status: z.literal("awaiting_approval"),
			pendingActionId: z.string(),
			summary: z.string(),
			previewData: z.record(z.string(), z.unknown()),
		}),
		(data, msg) => {
			msg.stripeActionApproval = {
				pendingActionId: data.pendingActionId,
				actionType: "cancel_subscription",
				summary: data.summary,
				preview: data.previewData,
				status: "pending",
			}
		},
	),
	proposeChangePlan: toolOutput(
		z.object({
			status: z.literal("awaiting_approval"),
			pendingActionId: z.string(),
			summary: z.string(),
			previewData: z.record(z.string(), z.unknown()),
		}),
		(data, msg) => {
			msg.stripeActionApproval = {
				pendingActionId: data.pendingActionId,
				actionType: "change_plan",
				summary: data.summary,
				preview: data.previewData,
				status: "pending",
			}
		},
	),
	proposeExtendTrial: toolOutput(
		z.object({
			status: z.literal("awaiting_approval"),
			pendingActionId: z.string(),
			summary: z.string(),
			previewData: z.record(z.string(), z.unknown()),
		}),
		(data, msg) => {
			msg.stripeActionApproval = {
				pendingActionId: data.pendingActionId,
				actionType: "extend_trial",
				summary: data.summary,
				preview: data.previewData,
				status: "pending",
			}
		},
	),
	runPostgresWhitelistedQuery: toolOutput(
		z.object({
			ok: z.literal(true),
			queryName: z.string(),
		}),
		(data, msg) => {
			const existing = msg.usedQueries ?? []
			if (existing.some((q) => q.name === data.queryName)) return
			msg.usedQueries = [...existing, { name: data.queryName }]
		},
	),
	proposePostgresQuery: toolOutput(
		z.discriminatedUnion("status", [
			z.object({
				status: z.literal("awaiting_approval"),
				pendingActionId: z.string(),
				summary: z.string(),
				previewData: z.object({
					sql: z.string(),
					reason: z.string().nullish(),
					whitelistSuggestion: z
						.object({
							name: z.string(),
							description: z.string(),
							sql: z.string(),
							parameters: z.array(
								z.object({
									name: z.string(),
									type: z.enum(["string", "number", "boolean", "timestamp"]),
								}),
							),
						})
						.nullish(),
				}),
			}),
			z.object({
				status: z.literal("auto_executed"),
				pendingActionId: z.string(),
				summary: z.string(),
				sql: z.string(),
				reason: z.string().nullish(),
				result: z.object({
					rowCount: z.number(),
					truncated: z.boolean(),
					columns: z.array(z.string()),
					rows: z.array(z.record(z.string(), z.unknown())),
					durationMs: z.number(),
				}),
			}),
		]),
		(data, msg) => {
			if (data.status === "auto_executed") {
				msg.postgresActionApproval = {
					pendingActionId: data.pendingActionId,
					sql: data.sql,
					reason: data.reason ?? null,
					summary: data.summary,
					status: "executed",
					whitelistSuggestion: null,
					result: data.result,
				}
				return
			}
			msg.postgresActionApproval = {
				pendingActionId: data.pendingActionId,
				sql: data.previewData.sql,
				reason: data.previewData.reason ?? null,
				summary: data.summary,
				status: "pending",
				whitelistSuggestion: data.previewData.whitelistSuggestion ?? null,
			}
		},
	),
	[AGENT_SETUP_TOOL_PROPOSE_LINEAR_ISSUE]: toolOutput(
		z.object({
			status: z.literal("awaiting_approval"),
			pendingActionId: z.string(),
			previewData: z.object({
				title: z.string(),
				description: z.string(),
				teamName: z.string().nullish(),
			}),
		}),
		(data, msg) => {
			msg.linearIssueProposal = {
				pendingActionId: data.pendingActionId,
				title: data.previewData.title,
				description: data.previewData.description,
				teamName: data.previewData.teamName ?? null,
				status: "pending",
			}
		},
	),
	[AGENT_SETUP_TOOL_PROPOSE_INSTRUCTIONS]: toolOutput(
		z.object({
			status: z.literal("awaiting_approval"),
			pendingActionId: z.string(),
			previewData: z.object({
				before: z.string().nullable(),
				after: z.string(),
				reason: z.string(),
			}),
		}),
		(data, msg) => {
			msg.agentInstructionsProposal = {
				pendingActionId: data.pendingActionId,
				before: data.previewData.before,
				after: data.previewData.after,
				reason: data.previewData.reason,
				status: "pending",
			}
		},
	),
	markIssueImportance: toolOutput(
		z.object({
			success: z.boolean(),
			id: z.string(),
			identifier: z.string(),
			title: z.string().nullish(),
			description: z.string().nullish(),
			important: z.boolean(),
			agentAuthored: z.boolean().nullish(),
		}),
		(data, msg, allMessages) => {
			// Starring a card must not blank it. The importance call carries no
			// wording of its own, so the card it re-renders inherits what the
			// card already on screen showed, down to a project's name.
			let linked: AiChatMessage["linearIssueLinked"]
			let own: AiChatMessage["linearIssueEdited"]
			for (const m of allMessages ?? []) {
				if (m.linearIssueLinked?.id === data.id) linked = m.linearIssueLinked
				if (m.linearIssueEdited?.id === data.id) own = m.linearIssueEdited
				else if (m.linearIssueCreated?.id === data.id)
					own = m.linearIssueCreated
			}

			if (linked) {
				msg.linearIssueLinked = {
					...linked,
					id: data.id,
					identifier: data.identifier,
					title: data.title ?? linked.title,
					description: data.description ?? linked.description,
					important: data.important,
				}
				return
			}
			msg.linearIssueEdited = {
				id: data.id,
				identifier: data.identifier,
				title: data.title ?? own?.title,
				description: data.description ?? own?.description,
				important: data.important,
				agentAuthored: data.agentAuthored ?? own?.agentAuthored ?? undefined,
			}
		},
	),
}

// Every card an assistant message can carry is stored, so a reopened chat shows
// what was proposed and done. Approval cards stay out on purpose: their pending
// action expires in Redis. ai-chat-cards.test.ts fails on an unlisted new card.
export const PERSISTED_ENRICHMENT_KEYS = [
	"contentRewrite",
	"linearIssueProposal",
	"agentInstructionsProposal",
	"linearIssueCreated",
	"linearIssueEdited",
	"linearIssueLinked",
	"linearIssueRemoved",
	"docsProposalSuggestion",
	"continueInClaude",
	"agentTodos",
	"usedQueries",
	"usedConnectors",
	"sources",
	"images",
	"feedback",
	"attachedContext",
	"emailRequested",
] as const

export type PersistedEnrichments = Partial<
	Pick<AiChatMessage, (typeof PERSISTED_ENRICHMENT_KEYS)[number]>
>

// The linked-issue cards, the citation chips and the rendered docs screenshots
// are the only enrichments a customer may see. Connector names, SQL, composer
// rewrites and docs proposals stay on internal surfaces. Cited docs are
// re-checked against the database on the way out, so an unlisted article never
// becomes a chip.
// The cards that carry Linear text, so every one of them is redacted on the
// way out. A new issue card joins the widget list through here or not at all.
const ISSUE_CARD_KEYS = [
	"linearIssueCreated",
	"linearIssueEdited",
	"linearIssueLinked",
	"linearIssueRemoved",
] as const

export const WIDGET_ENRICHMENT_KEYS = [
	...ISSUE_CARD_KEYS,
	"sources",
	"images",
	"emailRequested",
] as const satisfies readonly (typeof PERSISTED_ENRICHMENT_KEYS)[number][]

export type WidgetEnrichments = Partial<
	Pick<AiChatMessage, (typeof WIDGET_ENRICHMENT_KEYS)[number]>
>

/**
 * Drop the title and description unless the agent wrote them in this
 * conversation: a Linear issue carries several customers' reports and its own
 * text can name them. A linked roadmap project keeps what the portal already
 * publishes. A linked issue has no agent wording to fall back on, so it is
 * stripped whatever it claims, and so is a card stored before the flag existed.
 */
export function redactIssueCardText(card: unknown): unknown {
	if (!card || typeof card !== "object") return card
	const {
		title: _title,
		description: _description,
		...rest
	} = card as Record<string, unknown>
	if (rest.type === "project") return card
	if (rest.type === undefined && rest.agentAuthored === true) return card
	return rest
}

/// Narrows a stored enrichments blob to what the widget is allowed to render.
export function pickWidgetEnrichments(raw: unknown): WidgetEnrichments | null {
	if (!raw || typeof raw !== "object") return null
	const stored = raw as Record<string, unknown>
	const picked: Record<string, unknown> = {}
	for (const key of WIDGET_ENRICHMENT_KEYS) {
		if (stored[key] !== undefined && stored[key] !== null) {
			picked[key] = (ISSUE_CARD_KEYS as readonly string[]).includes(key)
				? redactIssueCardText(stored[key])
				: stored[key]
		}
	}
	return Object.keys(picked).length > 0 ? (picked as WidgetEnrichments) : null
}

// Citations live in the cite tool's *input*; its output is a bare
// acknowledgement, so they are read off the part instead of replayed.
export function extractCitedSources(
	parts: readonly { type: string; input?: unknown; [key: string]: unknown }[],
): AICiteItem[] {
	const byKey = new Map<string, AICiteItem>()
	for (const part of parts) {
		const isCite =
			part.type === `tool-${CITE_TOOL_ADD_SOURCE}` ||
			(part.type === "dynamic-tool" && part.toolName === CITE_TOOL_ADD_SOURCE)
		if (!isCite) continue
		const parsed = aiCiteToolInputSchema.safeParse(part.input)
		if (!parsed.success) continue
		for (const source of parsed.data.sources) {
			// A url the model wrote is dropped here and filled in from the database
			// on the way out, so a chip can never open an address it invented.
			const { url: _url, ...rest } = source
			byKey.set(`${rest.type}:${rest.id}`, rest)
		}
	}
	return [...byKey.values()]
}

// Derive the persistable enrichments from a finished assistant message's UI
// parts by replaying tool outputs through handleToolOutput. Returns null when
// no persistable enrichment was produced.
export function extractPersistableEnrichments(
	parts: readonly { type: string; output?: unknown; [key: string]: unknown }[],
	allMessages?: AiChatMessage[],
): PersistedEnrichments | null {
	const synthetic: AiChatMessage = {
		id: "",
		role: "assistant",
		content: "",
		processed: true,
		sources: [],
		actions: [],
		createdAt: new Date(0),
	}
	for (const part of parts) {
		if (typeof part.type !== "string" || part.output == null) continue
		// MCP tools are registered without schemas, so the sdk streams them as
		// `dynamic-tool` parts that carry the name in a field instead.
		if (part.type === "dynamic-tool") {
			const toolName =
				typeof part.toolName === "string" ? part.toolName : "dynamic-tool"
			handleToolOutput(toolName, part.output, synthetic, allMessages)
			continue
		}
		if (part.type.startsWith("tool-")) {
			const toolName = part.type.slice("tool-".length)
			handleToolOutput(toolName, part.output, synthetic, allMessages)
		}
	}
	synthetic.sources = extractCitedSources(parts)

	const enrichments: Record<string, unknown> = {}
	for (const key of PERSISTED_ENRICHMENT_KEYS) {
		const value = synthetic[key]
		if (value === undefined) continue
		// An empty list is not an enrichment: storing it would make a turn that
		// produced nothing look worth persisting.
		if (Array.isArray(value) && value.length === 0) continue
		enrichments[key] = value
	}
	return Object.keys(enrichments).length > 0
		? (enrichments as PersistedEnrichments)
		: null
}

const ENRICHMENT_HISTORY_NOTES: readonly {
	key: (typeof PERSISTED_ENRICHMENT_KEYS)[number]
	note: string
}[] = [
	{ key: "contentRewrite", note: "Applied an edit to the draft" },
	{ key: "docsProposalSuggestion", note: "Proposed a documentation change" },
	{ key: "linearIssueCreated", note: "Created a Linear issue" },
	{ key: "linearIssueEdited", note: "Edited a Linear issue" },
	{ key: "linearIssueLinked", note: "Linked a Linear issue" },
	{ key: "linearIssueRemoved", note: "Unlinked a Linear issue" },
	{ key: "linearIssueProposal", note: "Proposed a Linear issue" },
	{
		key: "agentInstructionsProposal",
		note: "Proposed a change to the agent instructions",
	},
	{ key: "usedQueries", note: "Ran a database query" },
	{ key: "continueInClaude", note: "Handed the task to Claude Code" },
	{ key: "agentTodos", note: "Showed the Agent's to-do list" },
	{ key: "images", note: "Rendered a screenshot" },
]

export function assistantHistoryText(
	content: string,
	enrichments: unknown,
): string {
	if (content.trim().length > 0) return content
	if (!enrichments || typeof enrichments !== "object") return ""
	const stored = enrichments as Record<string, unknown>
	return ENRICHMENT_HISTORY_NOTES.filter(({ key }) => {
		const value = stored[key]
		if (value == null) return false
		return Array.isArray(value) ? value.length > 0 : true
	})
		.map(({ note }) => `[${note}]`)
		.join(" ")
}

// A finished turn with neither text nor an enrichment has nothing to replay,
// and storing it as a blank assistant message makes providers reject every
// later request in that conversation.
export function shouldPersistAssistantMessage(
	text: string,
	enrichments: PersistedEnrichments | null,
): boolean {
	return text.trim().length > 0 || enrichments !== null
}

export function handleToolOutput(
	toolName: string,
	output: unknown,
	message: AiChatMessage,
	allMessages?: AiChatMessage[],
) {
	const attribution = readConnectorAttribution(output)
	if (attribution) {
		message.usedConnectors = dedupeConnectorAttributions([
			...(message.usedConnectors ?? []),
			attribution,
		])
	}

	const handler = toolOutputMap[toolName]
	if (!handler) return
	try {
		const toolOutput: unknown =
			typeof output === "string" ? JSON.parse(output) : output
		handler.handle(toolOutput, message, allMessages)
	} catch (error) {
		console.warn("Failed to handle tool output for", toolName, error)
	}
}

export interface ProcessAiChatStreamOptions {
	body: ReadableStream<Uint8Array<ArrayBufferLike>>
	aiAnswer: AiChatMessage
	chatHistory: AiChatMessage[]
	onUpdate: (messages: AiChatMessage[]) => void
	onSkippedChunk?: (error: unknown) => void
	errorMessage?: string
}

export async function processAiChatStream({
	body,
	aiAnswer,
	chatHistory,
	onUpdate,
	onSkippedChunk,
	errorMessage = "Failed to fetch AI response",
}: ProcessAiChatStreamOptions) {
	const reader = parseAiStream(body, onSkippedChunk).getReader()

	let isReading = true
	const toolNameByCallId = new Map<string, string>()

	// Every text block after the first is buffered until it either outgrows a
	// restatement or ends, so a block that only repeats the answer never renders.
	let pendingBlock: string | null = null
	const flushPendingBlock = () => {
		if (pendingBlock === null) return
		if (pendingBlock.trim()) aiAnswer.content += `\n\n${pendingBlock}`
		pendingBlock = null
	}
	const endPendingBlock = () => {
		if (pendingBlock === null) return
		if (isRestatement(pendingBlock, aiAnswer.content)) {
			pendingBlock = null
			return
		}
		flushPendingBlock()
	}
	let receivedFrame = false
	while (isReading) {
		const { value, done } = await reader.read()

		if (done) {
			isReading = false
			break
		}
		receivedFrame = true

		try {
			switch (value.type) {
				case "text-start":
					endPendingBlock()
					if (aiAnswer.content) pendingBlock = ""
					break
				case "text-delta":
					aiAnswer.waitAction = undefined
					if (pendingBlock !== null) {
						pendingBlock += value.delta
						// Past the buffer a block that no longer reads as a repeat
						// streams normally; one that still does stays held back, so a
						// long restatement never reaches the customer.
						if (
							pendingBlock.length > REDUNDANT_BLOCK_MAX_CHARS &&
							!isRestatement(pendingBlock, aiAnswer.content)
						) {
							flushPendingBlock()
						}
					} else {
						aiAnswer.content += value.delta
					}
					break
				case "text-end":
					endPendingBlock()
					break
				case "error":
					flushPendingBlock()
					// Text already streamed stays on screen; wiping it would throw
					// away an answer the user may have half-read.
					aiAnswer.errored = true
					aiAnswer.waitAction = undefined
					if (!aiAnswer.content) {
						aiAnswer.content = value.errorText?.trim() || errorMessage
					}
					break
				case "tool-input-available":
					if (value.toolName === "addActionButton") {
						const action = value.input as AIActionToolInput
						aiAnswer.actions = [...(aiAnswer.actions || []), action]
					} else if (value.toolName === "addSource") {
						const { sources } = value.input as AICiteToolInput
						aiAnswer.sources = [...(aiAnswer.sources || []), ...sources]
					}
					break
				case "tool-input-start":
					toolNameByCallId.set(value.toolCallId, value.toolName)
					aiAnswer.waitAction = getToolDisplayName(value.toolName)
					break
				case "tool-output-available": {
					const toolName = toolNameByCallId.get(value.toolCallId)
					// Connector output identifies itself, so it is handled even when the
					// call never announced a name.
					handleToolOutput(toolName ?? "", value.output, aiAnswer, chatHistory)
					if (toolName) toolNameByCallId.delete(value.toolCallId)
					break
				}
				case "tool-output-error":
					aiAnswer.waitAction = undefined
					break
				case AI_CHAT_HANDOFF_CHECK_PART: {
					const check = aiChatHandoffCheckSchema.parse(value.data)
					aiAnswer.handoffChecked = true
					aiAnswer.customerAskedForHuman = check.customerAskedForHuman
					break
				}
				default:
			}
		} catch (chunkError) {
			console.warn("Error processing stream chunk:", chunkError)
			aiAnswer.waitAction = undefined
		}
		onUpdate([...chatHistory, { ...aiAnswer }])
	}

	endPendingBlock()
	if (!receivedFrame && !aiAnswer.content.trim()) {
		aiAnswer.errored = true
		aiAnswer.waitAction = undefined
		aiAnswer.content = errorMessage
	}
	onUpdate([...chatHistory, { ...aiAnswer, processed: true }])
}
